import { useMemo, useState } from 'react'
import { capitalize } from 'lodash'
import {
  BadgeCheck,
  CheckCircle,
  Clock,
  Info,
  Pencil,
  XCircle,
} from 'lucide-react'

import { useExpensesStore } from '../store/expensesStore'
import { ExpensesStatusEnum } from '../types/expenses'
import { ExpenseCard } from '../components/ExpenseCard'
import { ExpenseForm } from '../components/ExpenseForm'
import { ExpenseDetails } from '../components/ExpenseDetails'
import type { expensesFilterSchema } from '@/schemas/filterSchemas'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ListPageHeader } from '@/components/custom/ListPageHeader'
import { PaginationWrapper } from '@/components/custom/PaginationWrapper'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { useAuthStore } from '@/features/auth/store/authStore'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useCleanQueryStates, usePagination } from '@/hooks'
import { usePermissions } from '@/utils/permissions'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/custom/badge'
import { formatDate, parseLocalDateString } from '@/utils/timeZoneUtils'
import { formatCurrency } from '@/utils/currencyUtils'

export default function Expenses() {
  const user = useAuthStore((state) => state.user)
  const permissions = usePermissions()

  const expenses = useExpensesStore((state) => state.expenses)
  const churches = useChurchesStore((state) => state.churches)

  const [query, setQuery] =
    useCleanQueryStates<typeof expensesFilterSchema>('/authed/expenses')

  const [open, setOpen] = useState({
    add: false,
    edit: false,
    detail: false,
  })
  const [expenseId, setExpenseId] = useState<string | null>(null)

  const filtered = useMemo(
    () =>
      expenses
        .filter((e) => {
          const matchSearch =
            query.search === '' ||
            e.requesterEmail
              .toLowerCase()
              .includes(query.search.toLowerCase()) ||
            e.requester.toLowerCase().includes(query.search.toLowerCase()) ||
            (e.refNumber &&
              e.refNumber.toLowerCase().includes(query.search.toLowerCase())) ||
            e.notes?.toLowerCase().includes(query.search.toLowerCase())

          const matchChurch =
            query.churchId.length === 0 ||
            (e.churchId && query.churchId.includes(e.churchId))

          const matchCategory =
            query.categoryId.length === 0 ||
            e.expenseItems.some((item) =>
              query.categoryId.includes(item.categoryId),
            )

          const matchStatus = !query.status || query.status === e.status

          // Date range filtering
          const expenseDate = new Date(e.date)
          const startDate = query.startDate
            ? parseLocalDateString(query.startDate)
            : null
          const endDate = query.endDate
            ? parseLocalDateString(query.endDate)
            : null

          const matchRange =
            (!startDate || expenseDate >= startDate) &&
            (!endDate || expenseDate <= endDate)

          return (
            matchSearch &&
            matchChurch &&
            matchCategory &&
            matchRange &&
            matchStatus
          )
        })
        .sort((a, b) => b.refNumber.localeCompare(a.refNumber)),
    [expenses, query, user, permissions],
  )

  const { page, pageCount, rows, setPage, goToFirstPage } = usePagination({
    data: filtered,
    pageSize: 10,
  })

  return (
    <div className="space-y-3">
      <ListPageHeader
        type="expenses"
        searchValue={query.search}
        onSearchChange={(value) => {
          setQuery({ search: value })
          goToFirstPage()
        }}
        initialFilters={query}
        onFilter={(filters) => {
          setQuery(filters)
          goToFirstPage()
        }}
        isFormOpen={open.add}
        onFormOpen={() => setOpen((o) => ({ ...o, add: true }))}
        onFormClose={() => setOpen((o) => ({ ...o, add: false }))}
        showAddButton={true}
      />

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-hidden rounded-md border bg-white">
        <Table>
          <THead>
            <TR>
              <TH>Ref.</TH>
              <TH>Expense Date</TH>
              <TH>Church</TH>
              <TH>Requester</TH>
              <TH>Email</TH>
              <TH className="text-right">Amount</TH>
              <TH>Status</TH>
              <TH>Created Date</TH>
              <TH className="text-right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((expense) => {
              const church = churches.find((c) => c._id === expense.churchId)

              const expenseUsers = [
                expense.localPastor?.userId,
                expense.adminTreasurer?.userId,
                expense.adminPastor?.userId,
              ]

              const isUserInvolved = user && expenseUsers.includes(user._id)
              return (
                <TR key={expense._id}>
                  <TD>{expense.refNumber}</TD>
                  <TD>{formatDate(expense.date, 'MMM D, YYYY')}</TD>
                  <TD>{church ? church.name : 'Unknown Church'}</TD>
                  <TD>{expense.requester}</TD>
                  <TD>{expense.requesterEmail}</TD>
                  <TD className="text-right">
                    {formatCurrency(expense.amount)}
                  </TD>
                  <TD>
                    <Badge
                      color={
                        expense.status === ExpensesStatusEnum.APPROVED
                          ? 'green'
                          : expense.status === ExpensesStatusEnum.CANCELLED
                            ? 'red'
                            : expense.status === ExpensesStatusEnum.UPDATING
                              ? 'blue'
                              : 'yellow'
                      }
                      icon={
                        expense.status === ExpensesStatusEnum.APPROVED
                          ? BadgeCheck
                          : expense.status === ExpensesStatusEnum.CANCELLED
                            ? XCircle
                            : expense.status === ExpensesStatusEnum.UPDATING
                              ? Pencil
                              : Clock
                      }
                    >
                      {expense.status === ExpensesStatusEnum.UPDATING
                        ? 'Needs Correction'
                        : capitalize(expense.status.toLowerCase())}
                    </Badge>
                  </TD>
                  <TD>{formatDate(expense.createdAt, 'MMM D, YYYY')}</TD>
                  <TD className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setExpenseId(expense._id)
                          setOpen({ ...open, detail: true })
                        }}
                      >
                        {expense.status === ExpensesStatusEnum.PENDING &&
                        isUserInvolved ? (
                          <>
                            <CheckCircle className="h-4 w-4" /> Review
                          </>
                        ) : (
                          <>
                            <Info className="h-4 w-4" /> Details
                          </>
                        )}
                      </Button>
                    </div>
                  </TD>
                </TR>
              )
            })}
            {rows.length === 0 && (
              <TR>
                <TD className="p-6 text-center text-neutral-500" colSpan={9}>
                  No results
                </TD>
              </TR>
            )}
          </TBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <ScrollArea className="lg:hidden h-[calc(100vh-170px)]">
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-lg border p-6 text-center text-neutral-500">
              No expenses found
            </div>
          ) : (
            filtered.map((expense) => (
              <ExpenseCard
                key={expense._id}
                expense={expense}
                onDetail={(id) => {
                  setExpenseId(id)
                  setOpen({ ...open, detail: true })
                }}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Desktop Pagination */}
      <div className="hidden lg:block">
        <PaginationWrapper
          pageCount={pageCount}
          currentPage={page}
          onPageChange={setPage}
        />
      </div>

      <ExpenseForm
        id={expenseId}
        open={open.add}
        type={open.edit ? 'edit' : 'add'}
        onClose={() => setOpen({ ...open, add: false })}
      />

      <ExpenseDetails
        id={expenseId}
        open={open.detail}
        onClose={() => setOpen({ ...open, detail: false })}
      />
    </div>
  )
}
